import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
CircularProgress,
Tabs,
Tab,
Alert
} from '@mui/material';
import { 
Delete, 
Inventory,
PictureAsPdf,
TableChart, 
GetApp,
Add,
Settings,
TrendingUp,
Warning,
CheckCircle,
Error,
} from '@mui/icons-material';
import {
X,
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
APIRestaurantList,
APIRestaurantStore,
APIRestaurantUpdate,
APIRestaurantDelete,
APIRestaurantStats,
APIUnitesNames,
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import {
formatCreatedAtDate, formatNumberWithComma,
    formatNumberWithComma2,
getCategoriesNameById,getStatusById,
getUserDetails,recordsPerPage, statusData
} from '../../../utils/helpers';
import {
exportToCSV,
exportToExcel,
exportToPDF
} from '../../../services/restaurant/RestaurantService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const RestaurantStock = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [statsLoading, setStatsLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [pageState, setPageState] = useState({
    pageData: [],
    branchNamesFilterData: [],
    restaurantStats: {
    total_restaurant_items: 0,
    total_quantity: 0,
    total_balance: 0,
    active_restaurant: 0,
    out_of_restaurant: 0,
    low_restaurant: 0,
    normal_restaurant: 0,
    high_restaurant: 0,
    most_valuable_item: 'N/A',
    most_valuable_value: 0
    }
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');

const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    description: '',
    uom: '',
    stock_date: '',
    branch_id: '',
    status: ''
});

const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: ''
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// Tab state for Add/Edit dialog
const [activeTab, setActiveTab] = useState(0);
const [referenceData, setReferenceData] = useState({
    branchNames: [],
    unitesNames: [],
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    apiService.setToastFunction(showToast);
}, [showToast]);

useEffect(() => {
    fetchData();
    fetchReferenceData();
}, []);

const fetchReferenceData = async () => {
    try {
    const [branchesResult, unRes] = await Promise.all([
        apiService.branchNames(),
        apiService.listNames(APIUnitesNames),
    ]);

    setReferenceData({
        branchNames: branchesResult.success ? branchesResult.data : [],
        unitesNames: unRes.success ? unRes.data : [],
    });
    } catch (error) {
    showToast("Error", "Failed to load reference data", "error");
    }
};

const fetchData = async (branchId = null) => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = branchId;
    if (!hasPermission(PERMISSIONS.BRANCHES)) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {};
    if (branchIdToUse) {
        params.branch_id = branchIdToUse;
    }

    const [pageRes, statsRes] = await Promise.all([
        axios.get(APIRestaurantList, { params }),
        axios.get(APIRestaurantStats, { params })
    ]);

    const restaurants = Array.isArray(pageRes.data.data) ? pageRes.data.data.reverse() : [];

    setPageState(prev => ({
        ...prev,
        pageData: restaurants,
        restaurantStats: statsRes.data.status === 'success' ? statsRes.data.data : prev.restaurantStats
    }));

    showToast("Success", TEXT_DATA_LOADED, "success");
    } catch (error) {
    let errorMessage = "Failed to load data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    }
    else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const fetchRestaurantStats = async () => {
    setStatsLoading(true);
    try {
    let branchIdToUse = selectedBranch;
    if (!hasPermission(PERMISSIONS.BRANCHES)) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {};
    if (branchIdToUse) {
        params.branch_id = branchIdToUse;
    }

    const response = await axios.get(APIRestaurantStats, { params });
    if (response.data.status === 'success') {
        setPageState(prev => ({
        ...prev,
        restaurantStats: response.data.data
        }));
    }
    } catch (error) {
    console.error("Error fetching restaurant stats:", error);
    } finally {
    setStatsLoading(false);
    }
};

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
    fetchRestaurantStats();
};

// Filter restaurants based on search term and filters
const filteredData = pageState.pageData.filter(restaurant => {
    const matchesSearch = searchTerm === '' ||
    restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    restaurant.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    restaurant.uom.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus = !filters.status ||
    restaurant.status == filters.status;

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const restaurantDate = new Date(restaurant.stock_date).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && restaurantDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const restaurantDate = new Date(restaurant.stock_date).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && restaurantDate <= filterToDate;
    }

    return matchesSearch && matchesStatus && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    name: '',
    quantity: '',
    description: '',
    uom: '',
    stock_date: new Date().toISOString().split('T')[0],
    branch_id: selectedBranch || getUserDetails("branchId"),
    status: ''
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
    setActiveTab(0);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    name: record.name,
    quantity: record.balance,
    description: record.description || '',
    uom: record.uom,
    stock_date: new Date(record.stock_date).toISOString().split('T')[0],
    branch_id: record.branch_id,
    status: record.status,
    });
    setActiveTab(0);
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    status: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

// Export handlers
const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

    const handleExportExcel = async () => {
    try {
        handleExportClose();
        await exportToExcel(filteredData, {
        branchNamesFilterData: referenceData.branchNames,
        unitesNames: referenceData.unitesNames
        }, filters);
        showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
        console.error('Excel export error:', error);
        showToast("Error", error.message, "error");
    }
    };

    const handleExportPDF = async () => {
    try {
        handleExportClose();
        await exportToPDF(filteredData, {
        branchNamesFilterData: referenceData.branchNames,
        unitesNames: referenceData.unitesNames
        }, filters);
        showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
        console.error('PDF export error:', error);
        showToast("Error", error.message, "error");
    }
    };

    const handleExportCSV = async () => {
    try {
        handleExportClose();
        await exportToCSV(filteredData, {
        branchNamesFilterData: referenceData.branchNames,
        unitesNames: referenceData.unitesNames
        });
        showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
        console.error('CSV export error:', error);
        showToast("Error", error.message, "error");
    }
    };

const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = referenceData.branchNames.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

// Tab navigation
const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const handleNextTab = () => {
    if (activeTab < 2) {
    setActiveTab(activeTab + 1);
    }
};

const handlePrevTab = () => {
    if (activeTab > 0) {
    setActiveTab(activeTab - 1);
    }
};

const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.uom) errors.push('Unit of Measurement is required');
    if (!formData.stock_date) errors.push('Stock date is required');
    if (!formData.status) errors.push('Status is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    
    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
    formDataToSend.append('branch_id', selectedBranch);
    } else {
    formDataToSend.append('branch_id', getUserDetails("branchId"));
    }
    
    // Add user_id (current user)
    formDataToSend.append('user_id', getUserDetails("Id"));
    
    // Add form data
    formDataToSend.append('name', formData.name);
    formDataToSend.append('quantity', formData.quantity);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('uom', formData.uom);
    formDataToSend.append('stock_date', formData.stock_date);
    formDataToSend.append('status', formData.status);

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }

    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APIRestaurantStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APIRestaurantUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APIRestaurantDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleCloseAdd();
        fetchData();
        fetchRestaurantStats();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Restaurant Stock Management ( {formatNumberWithComma2(pageState.pageData.length)} Total Items )
    </Typography>
    
    {/* Display selected branch name */}
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            fetchRestaurantStats();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    {/* Restaurant Statistics Cards */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Restaurant Items */}
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: 'primary.main',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Inventory sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Items
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.restaurantStats.total_restaurant_items || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                All restaurant items
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Current Balance */}
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: 'success.main',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Current Balance
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.restaurantStats.total_balance || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Total items available
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Active Items */}
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <Card elevation={3} sx={{ 
            borderRadius: 3,
            bgcolor: 'info.main',
            color: 'white',
            height: '100%'
        }}>
            <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 30, mr: 2 }} />
                <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Items
                </Typography>
                <Typography sx={{ fontWeight: 'bold' }}>
                    {statsLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 
                    formatNumberWithComma(pageState.restaurantStats.active_restaurant || 0)}
                </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Active items
            </Typography>
            </CardContent>
        </Card>
        </Grid>
    </Grid>

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Buttons */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Item
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Select Branch
            </Button>
            )}
    
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>

            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Export Data
            </Button>
            )}

            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={() => {
                fetchData();
                fetchRestaurantStats();
            }}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters */}
        {(filters.status || filters.dateFrom || filters.dateTo) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Stock Date</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>UOM</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((restaurant, index) => {
                return (
                    <TableRow
                    key={index}
                    hover
                    sx={{
                        '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                        }
                    }}
                    >
                    <TableCell>
                        <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        {new Date(restaurant.stock_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                        {restaurant.name}
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={formatNumberWithComma2(restaurant.balance)}
                        color={restaurant.balance > 0 ? "success" : "error"}
                        variant="filled"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={getCategoriesNameById(restaurant.uom, referenceData.unitesNames)}
                        color="info"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        {restaurant.status === 1 ? (
                        <Chip
                            label={getStatusById(restaurant.status)}
                            size="small"
                            color="primary"
                        />
                        ) : (
                        <Chip
                            label={getStatusById(restaurant.status)}
                            size="small"
                            color="error"
                        />
                        )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                        <TableCell>
                        <Chip
                            label={getCategoriesNameById(restaurant.branch_id, referenceData.branchNames) || 'N/A'}
                            color="info"
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                    )}
                    <TableCell align="center">
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenView(restaurant)}
                        color="primary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.info.light
                            }
                        }}
                        >
                        <ViewIcon />
                        </IconButton>
                        {hasPermission(PERMISSIONS.EDIT) && (
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenEdit(restaurant)}
                            color="secondary"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.success.light
                            }
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                        )}
                        {hasPermission(PERMISSIONS.DELETE) && (
                        <IconButton
                            edge="end"
                            onClick={() => handleOpenDelete(restaurant)}
                            color="error"
                            sx={{
                            '&:hover': {
                                backgroundColor: theme.palette.error.light
                            }
                            }}
                        >
                            <Delete />
                        </IconButton>
                        )}
                    </TableCell>
                    </TableRow>
                );
                })}
            </TableBody>
            </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Inventory sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No items found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new item</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {referenceData.branchNames.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter items only from that branch. 
            Leave unselected to view all items.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                handleBranchSelect(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Items
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Items
            </Button>

            <Divider>OR</Divider>

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value="">All Status</MenuItem>
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Stock Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog with Tabs */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ 
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "Add New Item" : "Edit Item"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="fullWidth"
            >
                <Tab 
                label="Basic Info" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Quantity" 
                sx={{ textTransform: 'none' }}
                />
                <Tab 
                label="Settings" 
                sx={{ textTransform: 'none' }}
                />
            </Tabs>
            </Box>

            {/* Tab 1: Basic Info */}
            {activeTab === 0 && (
            <Box>
                <Box>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Item Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Stock Date *"
                    type="date"
                    name="stock_date"
                    value={formData.stock_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    />
                </Grid>
                </Box>
            </Box>
            )}

            {/* Tab 2: Quantity */}
            {activeTab === 1 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Quantity *"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    required
                    />
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>UOM *</InputLabel>
                    <Select
                        name="uom"
                        value={formData.uom}
                        onChange={handleInputChange}
                        label="UOM *"
                    >
                        {referenceData.unitesNames.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.short_name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                </Box>
                {postStatus === "ADD" && (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        <strong>
                        The Quantity Will Be Set When Stock Inventory Is Made
                        </strong>
                    </Typography>
                    </Alert>
                </Grid>
                )}
            </Box>
            )}

            {/* Tab 3: Settings */}
            {activeTab === 2 && (
            <Box>
                <Box>
                <Grid item xs={12}>
                    <FormControl fullWidth required>
                    <InputLabel>Status *</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        label="Status *"
                    >
                        {statusData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                <br/>
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    />
                </Grid>
                </Box>
            </Box>
            )}
        </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
            {activeTab > 0 && (
            <Button
                color="primary"
                onClick={handlePrevTab}
                variant="outlined"
                sx={{ mr: 1 }}
            >
                Previous
            </Button>
            )}
        </Box>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
            {activeTab < 2 ? (
            <Button
                color="primary"
                onClick={handleNextTab}
                variant="contained"
            >
                Next
            </Button>
            ) : (
            <Button
                type="submit"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
            >
                {postStatus === "ADD" ? "Add Item" : "Update Item"}
            </Button>
            )}
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Item Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Item Name:</b> {selectedRecord.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>UOM:</b> {getCategoriesNameById(selectedRecord.uom, referenceData.unitesNames)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Current Balance:</b> {formatNumberWithComma(selectedRecord.balance)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Stock Date:</b> {new Date(selectedRecord.stock_date).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Status:</b>
                    {selectedRecord.status === 1 ? (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                    />
                    ) : (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                    />
                    )}
                </Typography>
                </Grid>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Branch:</b>
                    <Chip
                        label={getCategoriesNameById(selectedRecord.branch_id, referenceData.branchNames) || 'N/A'}
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                    />
                    </Typography>
                </Grid>
                )}
            </Grid>
            <Box>
                <br/>
                {selectedRecord.description && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2"><b>Description:</b> {selectedRecord.description}</Typography>
                </Grid>
                )}
                <br/>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
            </Box>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Item</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this item?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedRecord.name}
            </Typography>
            <Typography variant="body2">
                Current Balance: {formatNumberWithComma(selectedRecord.balance)} {selectedRecord.uom}
            </Typography>
            {selectedRecord.balance > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                This item still has balance. Deleting it may cause inventory discrepancies.
                </Alert>
            )}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default RestaurantStock;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});